/**
 * FCM web push — Node-only, no Django/Flutter counterpart. Phase 1 of the
 * notification roadmap (see refactor/docs/PARITY_NOTES.md).
 *
 * `firebase/app` and `firebase/messaging` are dynamically imported, same
 * reasoning as `jspdf` elsewhere in this codebase (lib/pdf.ts,
 * InvoiceDetailPage's handlePrintInvoice): most sessions never touch push,
 * so no page should pay for that bundle weight upfront.
 *
 * `isPushConfigured()` gates every other export — when any required
 * `VITE_FIREBASE_*` var is missing, this whole feature is treated as absent
 * (the UI hides the toggle entirely rather than showing a button that would
 * throw when clicked). No Firebase project has been provisioned for this
 * app yet, so that is the state in every environment today.
 */
import { registerPushToken, unregisterPushToken } from '../api/endpoints/push';

function firebaseConfig(): {
  apiKey: string;
  authDomain: string;
  projectId: string;
  messagingSenderId: string;
  appId: string;
} | null {
  const env = import.meta.env;
  const apiKey = env.VITE_FIREBASE_API_KEY;
  const authDomain = env.VITE_FIREBASE_AUTH_DOMAIN;
  const projectId = env.VITE_FIREBASE_PROJECT_ID;
  const messagingSenderId = env.VITE_FIREBASE_MESSAGING_SENDER_ID;
  const appId = env.VITE_FIREBASE_APP_ID;

  if (
    apiKey === undefined ||
    apiKey === '' ||
    authDomain === undefined ||
    authDomain === '' ||
    projectId === undefined ||
    projectId === '' ||
    messagingSenderId === undefined ||
    messagingSenderId === '' ||
    appId === undefined ||
    appId === ''
  ) {
    return null;
  }
  return { apiKey, authDomain, projectId, messagingSenderId, appId };
}

/** True only when every `VITE_FIREBASE_*` var this feature needs is set. */
export function isPushConfigured(): boolean {
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  return firebaseConfig() !== null && vapidKey !== undefined && vapidKey !== '' && 'serviceWorker' in navigator;
}

export type PushPermissionResult =
  | { ok: true }
  | { ok: false; reason: 'unconfigured' | 'unsupported' | 'permission-denied' | 'error' };

/**
 * Requests notification permission, registers the service worker, obtains
 * an FCM token, and registers it with the backend. Safe to call only after
 * `isPushConfigured()` — callers that skip the check get a clean
 * `{ok: false, reason: 'unconfigured'}` rather than a thrown error.
 */
export async function enablePushNotifications(): Promise<PushPermissionResult> {
  const config = firebaseConfig();
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (config === null || vapidKey === undefined || vapidKey === '') {
    return { ok: false, reason: 'unconfigured' };
  }
  if (!('serviceWorker' in navigator) || !('Notification' in window)) {
    return { ok: false, reason: 'unsupported' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: 'permission-denied' };
  }

  try {
    const { initializeApp } = await import('firebase/app');
    const { getMessaging, getToken } = await import('firebase/messaging');

    const app = initializeApp(config);
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });

    if (token === '') {
      return { ok: false, reason: 'error' };
    }

    await registerPushToken(token);
    localStorage.setItem('push-token', token);
    return { ok: true };
  } catch (error) {
    console.error('[push] failed to enable push notifications', error);
    return { ok: false, reason: 'error' };
  }
}

/** Unregisters the token this browser previously obtained, both from the browser and the backend. */
export async function disablePushNotifications(): Promise<void> {
  const token = localStorage.getItem('push-token');
  if (token === null) return;

  try {
    const config = firebaseConfig();
    if (config !== null) {
      const { initializeApp } = await import('firebase/app');
      const { getMessaging, deleteToken } = await import('firebase/messaging');
      const app = initializeApp(config);
      await deleteToken(getMessaging(app));
    }
  } catch (error) {
    console.error('[push] failed to delete the FCM token client-side', error);
  }

  await unregisterPushToken(token);
  localStorage.removeItem('push-token');
}

/** Whether THIS browser has already registered a token (best-effort, from localStorage — not a server round trip). */
export function hasRegisteredPushToken(): boolean {
  return localStorage.getItem('push-token') !== null;
}
